// syncQueue.js — Capa 5 del offline del chofer (v2: SEGURO, no pierde datos)
// Procesa la cola de operaciones pendientes enviándolas al servidor.
//
// PRINCIPIO CLAVE: ante la duda, NUNCA descartar una operación.
// Una entrega perdida es dinero perdido. Solo se borra de la cola si el
// servidor confirma que se guardó (2xx). Cualquier error → se mantiene y
// se reintenta. El usuario ve el contador de pendientes hasta que se logre.

import { leerCola, borrarDeCola, actualizarEnCola } from './offlineDB';

const LOG = true;
function log(...a) { if (LOG) console.log('[sync]', ...a); }

let _sincronizando = false;

// Envía UNA operación. Devuelve:
//   { estado: 'ok' }        -> se guardó (2xx) → borrar de la cola
//   { estado: 'reintentar' } -> error temporal o dudoso → mantener en cola
async function enviarOperacion(api, item) {
  try {
    let res;
    if (item.tipo === 'entrega') {
      res = await api.post(`/viajes/${item.viaje_id}/entregas`, item.payload);
    } else if (item.tipo === 'cierre') {
      res = await api.post(`/viajes/${item.viaje_id}/cerrar`, item.payload);
    } else {
      log('tipo desconocido', item.tipo, '→ mantener por seguridad');
      return { estado: 'reintentar' };
    }
    // Llegó respuesta 2xx: se guardó de verdad.
    log('guardada en servidor OK', item.temp_id);
    return { estado: 'ok' };
  } catch (err) {
    const status = err?.response?.status;
    // NUNCA descartamos por error. Todo error = reintentar y mantener en cola.
    // (Preferimos un posible duplicado a perder una entrega. Un duplicado se
    //  puede corregir; una entrega perdida es dinero perdido.)
    log('no confirmada (', status || 'sin-red', ') → mantener en cola', item.temp_id);
    return { estado: 'reintentar' };
  }
}

// Procesa la cola en orden. Solo borra las confirmadas (2xx).
export async function sincronizar(api) {
  if (_sincronizando) { log('ya sincronizando'); return null; }
  _sincronizando = true;
  let enviadas = 0, pendientes = 0;
  try {
    const cola = await leerCola();
    log('cola:', cola.length);
    for (const item of cola) {
      const r = await enviarOperacion(api, item);
      if (r.estado === 'ok') {
        await borrarDeCola(item.temp_id);
        enviadas++;
      } else {
        item.intentos = (item.intentos || 0) + 1;
        await actualizarEnCola(item);
        pendientes++;
        // Si una falla, cortar (probablemente sin red); se reintenta luego.
        break;
      }
    }
    const restantes = (await leerCola()).length;
    return { enviadas, pendientes, restantes };
  } finally {
    _sincronizando = false;
  }
}

// ─── Backoff programado ──────────────────────────────────────────────────────
const PASOS = [5000, 15000, 30000, 60000, 120000, 300000]; // 5s→5m
let _timer = null;
let _pasoIdx = 0;

export function detenerReintentos() {
  if (_timer) { clearTimeout(_timer); _timer = null; }
  _pasoIdx = 0;
}

export function programarReintentos(api, onResultado) {
  detenerReintentos();
  const ejecutar = async () => {
    const resumen = await sincronizar(api);
    if (onResultado) onResultado(resumen);
    const restantes = resumen ? resumen.restantes : 0;
    if (restantes > 0) {
      const delay = PASOS[Math.min(_pasoIdx, PASOS.length - 1)];
      _pasoIdx++;
      log('reintento en', delay / 1000, 's (restantes:', restantes, ')');
      _timer = setTimeout(ejecutar, delay);
    } else {
      log('cola vacía, detengo');
      detenerReintentos();
    }
  };
  ejecutar();
}

export default { sincronizar, programarReintentos, detenerReintentos };
